import { Injectable } from '@angular/core';
import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, firstValueFrom, from, shareReplay, switchMap, tap, throwError } from 'rxjs';

import { environment } from 'src/environments/environment';
import { AdminSessionService } from '../services/admin-session.service';
import { AdminRefreshResponse } from '../services/authentication.service';

@Injectable()
export class AdminAuthInterceptor implements HttpInterceptor {
  private readonly rawHttpClient: HttpClient;
  private refreshRequest$: Observable<AdminRefreshResponse> | null = null;

  constructor(
    private readonly router: Router,
    private readonly adminSessionService: AdminSessionService,
    httpBackend: HttpBackend,
  ) {
    this.rawHttpClient = new HttpClient(httpBackend);
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const accessToken = this.accessToken;
    const shouldSkipAuth = this.shouldSkipAuth(request.url);

    const authenticatedRequest = accessToken && !shouldSkipAuth
      ? this.addAuthorizationHeader(request, accessToken)
      : request;

    return next.handle(authenticatedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (!this.shouldRefreshAfterError(error, request)) {
          return throwError(() => error);
        }

        return this.handleRefreshFlow(authenticatedRequest, next, error);
      }),
    );
  }

  private handleRefreshFlow(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    originalError: HttpErrorResponse,
  ): Observable<HttpEvent<unknown>> {
    const refreshToken = this.refreshToken;
    if (!refreshToken) {
      this.forceLogout();
      return throwError(() => originalError);
    }

    const refreshRequest$ = this.refreshRequest$ ?? this.createRefreshRequest(refreshToken);

    return refreshRequest$.pipe(
      switchMap((response) => next.handle(this.addAuthorizationHeader(request, response.access))),
    );
  }

  private createRefreshRequest(refreshToken: string): Observable<AdminRefreshResponse> {
    const refreshRequest$ = from(this.refreshAccessToken(refreshToken)).pipe(
      tap((response) => {
        this.persistSession(response);
      }),
      catchError((refreshError) => {
        this.forceLogout();
        return throwError(() => refreshError);
      }),
      finalize(() => {
        this.refreshRequest$ = null;
      }),
      shareReplay(1),
    );

    this.refreshRequest$ = refreshRequest$;
    return refreshRequest$;
  }

  private async refreshAccessToken(refresh: string): Promise<AdminRefreshResponse> {
    return firstValueFrom(
      this.rawHttpClient.post<AdminRefreshResponse>(
        `${environment.apiUrl}auth/refresh/`,
        { refresh },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      ),
    );
  }

  private addAuthorizationHeader(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private shouldSkipAuth(url: string): boolean {
    return url.includes('/auth/login/') || url.includes('/auth/refresh/');
  }

  private shouldRefreshAfterError(error: HttpErrorResponse, request: HttpRequest<unknown>): boolean {
    if (this.shouldSkipAuth(request.url)) {
      return false;
    }

    return error.status === 401 && this.isAdminAuthenticated;
  }

  private forceLogout(): void {
    this.adminSessionService.clearSession();
    void this.router.navigateByUrl('/admin/login');
  }

  private persistSession(response: AdminRefreshResponse): void {
    this.adminSessionService.persistRefreshSession(response);
  }

  private get accessToken(): string | null {
    return this.adminSessionService.accessToken;
  }

  private get refreshToken(): string | null {
    return this.adminSessionService.refreshToken;
  }

  private get isAdminAuthenticated(): boolean {
    return this.adminSessionService.isAuthenticated;
  }
}
